import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { Flexbox, FlexboxVertical } from '../../Layout';
import styled from 'styled-components';
import { Headline2, TextDefault } from '../../Text';
import { Colors } from '../../../../styles/colors';
import { SuggestionDropDown } from '../../forms/SuggestionDropDown';
import { CustomerForm } from '../../../forms/CustomerForm';
import { SimpleAccordion } from '../../SimpleAccordion';
import { Customer } from '../../../../interfaces/Customer';
import { Icon } from '../../Icon';
import { AccordionSection } from '../../AccordionSection';

const Headline = styled(Headline2)`
	color: ${Colors.primary};
`;

const HeaderIcon = styled.img`
  width: auto;
  height: 7rem;
  object-fit: contain;
`;

const HeadlineWrapper = styled(Flexbox)`
  align-items: flex-start;
  margin-bottom: 3rem;

  ${Headline} {
    flex: 1;
    padding-right: 3.5rem;
  }
`;

const CustomerEditFormWrapper = styled.div`
  padding: 2rem 0 0 0;
  
  ${Flexbox} {
    cursor: pointer;

    @media (pointer: fine) {
      &:hover {
        color: ${Colors.tertiary};
      }
    }

    & > *:first-child {
      margin-right: .5rem;
    }
  }
`;

interface Props {
  customers: Customer[];
  selectedCustomerState: [Customer | null, Dispatch<SetStateAction<Customer | null>>];
  onCreateCustomer?: (customer: Customer) => boolean;
  onUpdateCustomer?: (customer: Customer) => boolean;
}
export const QuestionnaireCustomerSelection: FC<Props> = ({ customers, selectedCustomerState, onCreateCustomer, onUpdateCustomer, ...props }) => {
  const [selectedCustomer, setSelectedCustomer] = selectedCustomerState;
  const [accordionState, setAccordionState] = useState<number>(Boolean(selectedCustomer) ? 1 : 0);
  const [editCustomerVisible, setEditCustomerVisible] = useState(false);

  useEffect(() => {
    if (accordionState !== 1 && editCustomerVisible) {
      setEditCustomerVisible(false);
    }
  }, [accordionState]);

  return (
    <>
    <HeadlineWrapper>
      <Headline>In wenigen Schritten zu Ihrer umfassenden Bedarfsanalyse</Headline>
      <HeaderIcon alt='' src={'./assets/images/analysis.svg'} />
    </HeadlineWrapper>

    <SimpleAccordion title={'Vorhandenen Kunden auswählen'} collapsed={accordionState !== 1} onClick={() => setAccordionState(accordionState !== 1 ? 1 : 0)}>
      <SuggestionDropDown
        id={'customer-suggestions'}
        onChange={({ id, text }) => {
          const customer = customers.find((customer) => customer.id === id);
          if (customer) {
            setSelectedCustomer(customer);    // set selection
          }
          else setSelectedCustomer(null);    // fallback
        }}
        value={selectedCustomer && { id: selectedCustomer.id, text: selectedCustomer.name } || null}
        suggestions={customers.map(({ id, name }) => ({
          id,
          text: name,
        }))}
        inline={false}
      />

      {     // update customer form
        selectedCustomer && onUpdateCustomer &&
        <CustomerEditFormWrapper>
          <Flexbox onClick={() => setEditCustomerVisible(!editCustomerVisible)}>
            <TextDefault>{ editCustomerVisible ? 'Bearbeiten abbrechen' : 'Kundendaten bearbeiten' }</TextDefault>
            <Icon icon={ editCustomerVisible ? 'close' : 'edit' } />
          </Flexbox>
          <AccordionSection collapsed={!(accordionState === 1 && editCustomerVisible)}>
            <CustomerEditFormWrapper>
              <CustomerForm
                showResponsibleConsultantSelection={false}
                customer={selectedCustomer}
                setCustomer={(customer) => {
                  if (onUpdateCustomer && onUpdateCustomer(customer)) {
                    setEditCustomerVisible(false);
                    setSelectedCustomer(customer);
                  }
                }}
                onCancel={() => setEditCustomerVisible(false)}
              />
            </CustomerEditFormWrapper>
          </AccordionSection>
        </CustomerEditFormWrapper>
      }
    </SimpleAccordion>

    <SimpleAccordion title={'Neuen Kunden anlegen'} collapsed={accordionState !== 2} onClick={() => setAccordionState(accordionState !== 2 ? 2 : 0)}>
      <CustomerForm setCustomer={(customer) => {
        if (onCreateCustomer && onCreateCustomer(customer)) {
          setSelectedCustomer(customer);
          setAccordionState(1);   // collapse accordion
        }
      }} />
    </SimpleAccordion>
    </>
  )
}
